<template>
  <div class="react-app-host">
    <CAlert v-if="error" color="danger">
      Failed to load the React micro-frontend: {{ error }}
      <div class="small mt-1">Is <code>tf-frontend</code> running on {{ entryHint }}?</div>
    </CAlert>
    <!-- Covers the container's own blank window (qiankun fetching/executing the remote entry,
    then React mounting) independently of the host's global `isLoading` loader - self-contained
    so it doesn't depend on some other action having dispatched showLoader beforehand. Keyed off
    `mounting` rather than `!handle`: `handle` is assigned before `mountPromise` resolves (see
    mountApp below), so gating on it alone would hide this while React is still mid-mount. -->
    <LoadingOverlay v-if="mounting && !error" />
    <div v-show="!error" ref="microAppContainer" class="react-app-container" />
  </div>
</template>

<script>
import { mountTfFrontend } from "@/qiankun";

export default {
  name: "ReactAppHost",
  data() {
    return {
      handle: null,
      error: null,
      teardown: false,
      lastHandledPath: null,
      mounting: true,
    };
  },
  computed: {
    entryHint() {
      return process.env.VUE_APP_REACT_ENTRY || "http://localhost:3000";
    },
  },
  async mounted() {
    this.lastHandledPath = this.$route.path;
    await this.mountApp(this.$route.fullPath);
  },
  // "/react/register" (pre-auth, no `authorize` meta) and "/react/*" (post-auth pages —
  // dashboard, profile, ...) are two SEPARATE named route records that both happen to render
  // this same component (see router/index.js). Vue's core renderer reuses the one already-
  // mounted instance for either kind of transition, since it's literally the same component
  // at the same <router-view> slot either way — but vue-router's in-component `beforeRouteUpdate`
  // guard only fires for navigations that stay within the SAME matched route record (e.g.
  // /react/dashboard -> /react/profile). Crossing from "React App Register" to "React App
  // Pages" (e.g. right after registration's auto-login pushes /react/dashboard) never fires
  // it — so with only that guard, this component would sit there silently reusing its stale
  // instance forever: the address bar and $route update, but nothing here ever finds out.
  // Watching $route.path instead reacts to every change regardless of which record(s) are
  // involved, so it covers both cases uniformly.
  //
  // Either way, the already-mounted micro-app baked its initial page choice into its own
  // in-memory router at mount time and has no way to know the host URL moved, so remount it
  // on every EXTERNAL path change, explicitly telling it the new target path (see mountApp).
  //
  // "External" excludes the case where React itself navigated internally (e.g. a Link from
  // dashboard to profile) and asked us, via the host bridge, to mirror that onto our own URL
  // — the mounted app already knows about that page; remounting it here would be pointless
  // churn (and would fire right back into a loop of sorts). isReactOriginatedPath tells the
  // two apart.
  //
  // Kept alongside the $route.path watcher below rather than replaced by it: for the case it
  // DOES cover (same-record transitions), it runs as a proper navigation guard — teardown+mount
  // finish before `next()` resolves the navigation, so the URL only flips once the new page is
  // actually ready, with no stale-content flash. The watcher is strictly a fallback for
  // transitions this guard structurally cannot see; `lastHandledPath` lets it recognize a path
  // this guard already handled and skip doing it again.
  async beforeRouteUpdate(to, from, next) {
    if (to.path !== from.path) {
      // isReactOriginatedPath compares against the FULL path (query included) that the
      // sub-app's host/navigate dispatch recorded - `to.path` alone drops the query string,
      // which made this comparison always miss for any React-originated navigation whose
      // target URL carries a query param (e.g. "/react/profile?tab=personal_profile"),
      // triggering a pointless-and-wrong teardown+remount here.
      const reactOriginated =
        this.handle && this.handle.bridge.isReactOriginatedPath(to.fullPath);
      if (!reactOriginated) {
        await this.teardownApp();
        // Pass the target path explicitly rather than letting the sub-app read
        // window.location itself: this guard runs BEFORE vue-router calls pushState (guards
        // resolve first, the URL only flips once they all call next() - see confirmTransition
        // in vue-router's source), so window.location.pathname is still the OLD path here.
        // Seeding the remounted micro-app from that stale value is what left it showing the
        // previous page's content after the address bar had already moved on.
        await this.mountApp(to.fullPath);
      }
      this.lastHandledPath = to.path;
    }
    next();
  },
  watch: {
    async '$route.path'(path) {
      if (path === this.lastHandledPath) return;
      this.lastHandledPath = path;
      const reactOriginated =
        this.handle && this.handle.bridge.isReactOriginatedPath(this.$route.fullPath);
      if (!reactOriginated) {
        await this.teardownApp();
        await this.mountApp(this.$route.fullPath);
      }
    },
  },
  async beforeDestroy() {
    this.teardown = true;
    await this.teardownApp();
  },
  methods: {
    async mountApp(initialPath) {
      this.error = null;
      this.teardown = false;
      this.mounting = true;
      try {
        const handle = await mountTfFrontend({
          container: this.$refs.microAppContainer,
          store: this.$store,
          router: this.$router,
          initialPath,
        });
        // The route may have changed again while the entry HTML/JS was still downloading.
        if (this.teardown) {
          await handle.destroy();
          return;
        }
        this.handle = handle;
        // `handle` is already set above, but the container is only actually painted once this
        // resolves - keep `mounting` (and therefore the overlay) true until then, not just
        // until `handle` exists.
        await handle.microApp.mountPromise;
      } catch (e) {
        this.error = (e && e.message) || String(e);
      } finally {
        this.mounting = false;
        // Takes back down the loader React's host/showLoader (e.g. loginAfterRegistration) may
        // have raised before this mount started — whatever caused it, the new page (or error
        // state) is now actually on screen, so nothing should still be covering it. Harmless
        // no-op when nothing raised it.
        this.$store.dispatch("hideLoader");
      }
    },
    async teardownApp() {
      if (this.handle) {
        const handle = this.handle;
        this.handle = null;
        await handle.destroy();
      }
    },
  },
};
</script>

<style scoped>
.react-app-container {
  min-height: 70vh;
}
.react-app-host {
  margin-left: -30px;
  margin-right: -30px;
  margin-top: -1rem;
}
</style>
